// src/components/Page.js
import React, { lazy, Suspense, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import Paper from '@mui/material/Paper';
import CssBaseline from '@mui/material/CssBaseline';
import getTheme from 'theme';
import AOS from 'aos';
import { ThemeProvider, useThemeContext } from '../contexts/ThemeContext';

import config from '../config/env';

/**
 * Dev-only, so it must not be in the production bundle.
 *
 * The render below was already gated on `isLocal || isDevelopment`, but a
 * static import is resolved at build time -- the gate decided whether to
 * *render* it, never whether to *ship* it. It pulled react-color and
 * tinycolor2 into the initial chunk (94 KB raw) for every production user, to
 * back a panel none of them can open. `lazy` makes the import match the gate.
 */
const ThemeConfigurator = lazy(() => import('./ThemeConfigurator'));

const PageContent = ({ children }) => {
  const { themeMode, customTheme } = useThemeContext();
  const theme = useMemo(() => getTheme(themeMode, customTheme), [
    themeMode,
    customTheme,
  ]);

  return (
    <MuiThemeProvider theme={theme}>
      <EmotionThemeProvider theme={theme}>
        <CssBaseline />
        {/*
          This Paper is the app canvas, not a card. `Paper` defaults to
          `background.paper` (the white card token), which left every screen
          sitting on white; Indigo / Inter puts the card on top of the tinted
          `--background`, so the canvas takes `background.default` instead.
        */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: 'background.default',
            color: 'text.primary',
            minHeight: '100vh',
          }}
        >
          {children}
          {config.isLocal || config.isDevelopment ? (
            <Suspense fallback={null}>
              <ThemeConfigurator />
            </Suspense>
          ) : null}
        </Paper>
      </EmotionThemeProvider>
    </MuiThemeProvider>
  );
};

const Page = ({ children }) => {
  const { themeMode } = useThemeContext();

  useEffect(() => {
    // Initialize AOS
    AOS.init({
      once: true,
      delay: 0,
      duration: 800,
      offset: 0,
      easing: 'ease-in-out',
    });

    // Remove server-side injected CSS
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);

  useEffect(() => {
    AOS.refresh();
  }, [themeMode]);

  return <PageContent>{children}</PageContent>;
};

Page.propTypes = {
  children: PropTypes.node.isRequired,
};

const PageWithProvider = ({ children }) => (
  <ThemeProvider>
    <Page>{children}</Page>
  </ThemeProvider>
);

PageWithProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

PageContent.propTypes = {
  children: PropTypes.node.isRequired,
};

export default PageWithProvider;
